import React, { useEffect, useState } from 'react'
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
} from 'react-bootstrap'
import CustomBreadcrumb from '../../../components/CustomBreadcrumb'
import {
  checkCircleIcon,
  checkTickIcon,
  homeIcon,
  refreshIcon,
  searchIcon,
} from '../../../constants/icons'
import AllComponent from './AllComponent'
import ExamPaperTableSkeleton from './ExamPaperTableSkeleton'
import BookmarkedComponent from './BookmarkedComponent'
import FilterTopicTree from './FilterTopicTree'
import BasicContentLoader from '../../../components/loader/BasicContentLoader'
import SelectedDropdown from '../../../components/dropdown/SelectedDropdown'
import ToastAlerts from '../../../components/toast/ToastAlerts'
import ToastSnackbarWrapper from '../../../components/toast/ToastSnackbarWrapper'
import CustomPagination from '../../../components/pagination'
import { getSubjectDetailsById } from '../../../api/user/subject.api'
import { searchQuestions } from '../../../api/user/question.api'
import { getAllBookmarks, toggleBookmark } from '../../../api/user/bookmark.api'
import { useLocation, useNavigate } from 'react-router-dom'
import QuestionDetailsModal from '../../../components/modal/QuestionDetailsModal'
import { getQuestionById } from '../../../api/user/question.api'
import {
  FaChevronDown,
  FaChevronUp,
  FaColumns,
  FaListUl,
  FaSlidersH,
} from 'react-icons/fa'
import {
  buildPickerTreeFromSubjectDetail,
  buildSelectedChaptersMapFromRouteState,
  buildQuestionTreeSearchTopicsPayload,
  EXAM_SYLLABUS_LEVELS,
} from '../../../utils/subjectSyllabusTree'

/**
 * Builds `/api/user/questions` filters. Server ANDs each param; within a param, comma ids are OR ($in).
 * We send only the tiers that match how the user arrived or refined — avoids empty results when
 * questions are not tagged at every level.
 *
 * Empty sidebar: subjectId only for syllabus scope (+ mark / year / search / pagination).
 * Sidebar Apply: subjectId + topicIds (union of checked nodes’ topicId) + chapter / subtopic / section ids.
 */
function buildExamPaperQuestionSearchBody({
  locationState,
  page,
  pageSize,
  searchText,
  selectedMark,
  selectedYear,
  selectedChapters,
  syllabusTree,
}) {
  const subjectId = locationState?.subjectId

  const body = { page, limit: pageSize }

  if (subjectId) {
    body.subjectId = String(subjectId)
  }
  if (selectedMark) {
    body.marks = Number(selectedMark)
  }
  if (selectedYear) {
    body.year = Number(selectedYear)
  }
  if (searchText?.trim()) {
    body.searchText = searchText.trim()
  }

  const hasAnyChecked = Object.values(selectedChapters || {}).some(Boolean)
  if (hasAnyChecked) {
    if (syllabusTree?.length) {
      const { allUnitsSelected, topics } = buildQuestionTreeSearchTopicsPayload(
        selectedChapters,
        syllabusTree,
      )
      if (!allUnitsSelected && Array.isArray(topics) && topics.length > 0) {
        body.topics = topics
      }

      console.log('[ExamPapers] question search body', {
        mode: allUnitsSelected ? 'sidebar-all-units' : 'sidebar',
        allUnitsSelected,
        body,
      })
      return body
    }

    console.log('[ExamPapers] question search body', {
      mode: 'sidebar-fallback-no-tree',
      note: 'No syllabus tree; sending subject-only scope.',
      body,
    })

    return body
  }

  console.log('[ExamPapers] question search body', {
    mode: 'subject-only-empty-sidebar',
    note: 'No syllabus checkboxes on; syllabus scope is subject only',
    body,
  })

  return body
}

function FliterPappers() {
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [questions, setQuestions] = useState([])
  const [subjectTitle, setSubjectTitle] = useState('')
  const [syllabusTree, setSyllabusTree] = useState([])
  const [searchText, setSearchText] = useState('')
  const [selectedChapters, setSelectedChapters] = useState({})
  const [appliedChapters, setAppliedChapters] = useState({})
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  })
  const [loadMoreLoading, setLoadMoreLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [selectedPapers, setSelectedPapers] = useState([])
  const [showToastSnackbar, setShowToastSnackbar] = useState(false)
  const [bookmarkLoading, setBookmarkLoading] = useState(false)
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState([])
  const [bookmarkPagination, setBookmarkPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  })
  const [allBookmarkedIds, setAllBookmarkedIds] = useState(new Set())
  /** 'single' | 'two' — wired to grid later; UI only for now */
  const [resultsLayout, setResultsLayout] = useState('single')
  /** Below lg, syllabus sidebar stacks full-width; start collapsed so results stay visible */
  const [mobileSyllabusOpen, setMobileSyllabusOpen] = useState(false)

  // Modal states and functions, moved from AllComponent.js
  const [showModal, setShowModal] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const [questionDetailLoading, setQuestionDetailLoading] = useState(false)

  const location = useLocation()

  const navigate = useNavigate()

  useEffect(() => {
    if (!location.state?.subjectId || !location.state?.topicId) {
      navigate('/exam-papers')
    }
  }, [location.state, navigate])

  const fetchAllBookmarkedQuestions = async () => {
    try {
      let allIds = new Set()
      let currentPage = 1
      let totalPages = 1

      do {
        const response = await getAllBookmarks(currentPage, 100) // Fetch with a larger limit if possible, or iterate
        response.data.forEach((b) => allIds.add(b.question._id))
        totalPages = response.pagination.totalPages
        currentPage++
      } while (currentPage <= totalPages)

      setAllBookmarkedIds(allIds)
    } catch (error) {
      console.error('Failed to fetch all bookmark IDs:', error)
    }
  }

  useEffect(() => {
    fetchAllBookmarkedQuestions()
  }, [])

  const fetchBookmarkedQuestions = async (page, search = searchText) => {
    setBookmarkLoading(true)
    try {
      const response = await getAllBookmarks(
        page,
        bookmarkPagination.pageSize,
        search,
      )
      setBookmarkedQuestions(
        response.data.map((b) => b.question).filter(Boolean),
      )
      setBookmarkPagination(response.pagination)
      window.scrollTo(0, 0)
    } catch (error) {
      console.error('Failed to fetch bookmarked questions:', error)
    } finally {
      setBookmarkLoading(false)
    }
  }

  useEffect(() => {
    fetchBookmarkedQuestions(bookmarkPagination.currentPage)
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const subjectId = location.state?.subjectId
        const topicId = location.state?.topicId
        let treeForFilters = []

        if (subjectId) {
          const fetchedSubjectDetails = await getSubjectDetailsById(subjectId)
          setSubjectTitle(fetchedSubjectDetails.data.title)
          treeForFilters = buildPickerTreeFromSubjectDetail(fetchedSubjectDetails)
          setSyllabusTree(treeForFilters)
          console.log('Fetched Subject Details:', fetchedSubjectDetails.data)
        } else {
          console.warn('No subjectId found in route state.')
          setSyllabusTree([])
        }

        const routeSelectedChapters = buildSelectedChaptersMapFromRouteState(
          location.state,
          treeForFilters,
        )
        setSelectedChapters(routeSelectedChapters)
        setAppliedChapters(routeSelectedChapters)

        if (topicId) {
          const fetchedQuestions = await searchQuestions(
            buildExamPaperQuestionSearchBody({
              locationState: location.state,
              page: pagination.currentPage,
              pageSize: pagination.pageSize,
              searchText: '',
              selectedMark: '',
              selectedYear: '',
              selectedChapters: routeSelectedChapters,
              syllabusTree: treeForFilters,
            }),
          )
          setQuestions(fetchedQuestions.data)
          setPagination(fetchedQuestions.pagination)
          console.log('Fetched Questions:', fetchedQuestions.data)
        } else {
          console.warn(
            'No topicId found in route state. Questions not fetched.',
          )
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setInitialLoading(false)
      }
    }
    fetchData()
  }, [location.state])

  const breadcrumbItems = [
    { label: <>{homeIcon}</>, path: '/' },
    { label: 'All Subjects', path: '/exam-papers' },
    { label: 'Maths(H)', path: '/exam-papers/mathematics' },
  ]

  const loaders = [1, 2, 3, 4]

  const marks = Array.from({ length: 100 }, (_, i) => i + 1)
  const currentYear = new Date().getFullYear()
  const [selectedMark, setSelectedMark] = useState('')
  const [selectedYear, setSelectedYear] = useState('')

  const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) =>
    (2000 + i).toString(),
  ).reverse()

  const handleMarkSelect = (mark) => {
    setSelectedMark(mark)
    handleApplyFilters(mark, selectedYear)
  }

  const handleYearSelect = (year) => {
    setSelectedYear(year)
    handleApplyFilters(selectedMark, year)
  }

  const handleApplyFilters = async (
    mark = selectedMark,

    year = selectedYear,
  ) => {
    setLoading(true)
    try {
      const body = buildExamPaperQuestionSearchBody({
        locationState: location.state,
        page: 1,
        pageSize: pagination.pageSize,
        searchText,
        selectedMark: mark,
        selectedYear: year,
        selectedChapters,
        syllabusTree,
      })
      const fetchedQuestions = await searchQuestions(body)
      setQuestions(fetchedQuestions.data)
      setPagination(fetchedQuestions.pagination)
      setPagination((prev) => ({ ...prev, currentPage: 1 }))
      setAppliedChapters(selectedChapters)
      setMobileSyllabusOpen(false)
    } catch (error) {
      console.error('Error applying filters:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLoadMore = async () => {
    setLoadMoreLoading(true)
    const nextPage = pagination.currentPage + 1
    const scrollY = window.scrollY
    try {
      const body = buildExamPaperQuestionSearchBody({
        locationState: location.state,
        page: nextPage,
        pageSize: pagination.pageSize,
        searchText,
        selectedMark,
        selectedYear,
        selectedChapters: appliedChapters,
        syllabusTree,
      })
      const fetchedQuestions = await searchQuestions(body)
      setQuestions((prevQuestions) => {
        // Restore scroll position after state update and re-render
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY)
        })
        return [...prevQuestions, ...fetchedQuestions.data]
      })
      setPagination(fetchedQuestions.pagination)
    } catch (error) {
      console.error('Error loading more questions:', error)
      // Optionally show an error toast
    } finally {
      setLoadMoreLoading(false)
    }
  }

  const handleSaveCollection = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setShowToastSnackbar(false)
      setActiveTab('bookmarked')
    }, 2000)
  }

  const handleBookmarkClick = async (e, question, isRemoving) => {
    e.stopPropagation()

    try {
      const data = await toggleBookmark(question._id)

      setToastMessage(data?.message)

      setShowToast()

      // Update the allBookmarkedIds set

      setAllBookmarkedIds((prevIds) => {
        const newIds = new Set(prevIds)

        if (isRemoving) {
          newIds.delete(question._id)
        } else {
          newIds.add(question._id)
        }

        return newIds
      })

      // If the user removes the last item from a page, go to the previous page

      if (
        isRemoving &&
        bookmarkedQuestions.length === 1 &&
        bookmarkPagination.currentPage > 1
      ) {
        fetchBookmarkedQuestions(bookmarkPagination.currentPage - 1, searchText)
      } else {
        fetchBookmarkedQuestions(bookmarkPagination.currentPage, searchText)
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error)
    }
  }

  // Moved from AllComponent.js
  const handleQuestionClick = async (question) => {
    setShowModal(true)
    setQuestionDetailLoading(true)
    try {
      const response = await getQuestionById(question._id)
      setSelectedQuestion(response.data)
    } catch (error) {
      console.error('Failed to fetch question details:', error)
    } finally {
      setQuestionDetailLoading(false)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedQuestion(null)
  }

  return (
    <>
      <ToastAlerts
        showToast={showToast}
        setShowToast={setShowToast}
        icon={checkCircleIcon}
        text={toastMessage}
        toastVariant='success'
      />

      <ToastSnackbarWrapper
        selectedPapers={selectedPapers}
        setSelectedPapers={setSelectedPapers}
        showToastSnackbar={showToastSnackbar}
        setActiveTab={setActiveTab}
        handleSaveCollection={handleSaveCollection}
      />

      <div className='breadcrum-header'>
        <Container>
          <CustomBreadcrumb breadcrumbItems={breadcrumbItems} />
        </Container>
      </div>

      <div className='filter-section'>
        <Container>
          <Row className='filter-header'>
            <Col lg={4} xl={3}>
              <h4>{subjectTitle}</h4>
            </Col>
            <Col lg={8} xl={9}>
              <div className='filter-header-wrap'>
                <div className='filter-tab'>
                  <span
                    className={`filter-tab-btn ${
                      activeTab === 'all' ? 'active' : ''
                    }`}
                    role='button'
                    onClick={() => setActiveTab('all')}
                  >
                    All ({pagination.totalItems})
                  </span>
                  <span
                    className={`filter-tab-btn ${
                      activeTab === 'bookmarked' ? 'active' : ''
                    } `}
                    role='button'
                    onClick={() => setActiveTab('bookmarked')}
                  >
                    Bookmarked ({bookmarkPagination?.totalItems})
                  </span>
                </div>
                <InputGroup className='search-form'>
                  <InputGroup.Text>{searchIcon}</InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search papers by keywords'
                    className=' mr-sm-2'
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        if (activeTab === 'all') {
                          handleApplyFilters(selectedMark, selectedYear)
                        } else {
                          fetchBookmarkedQuestions(1, searchText)
                        }
                      }
                    }}
                  />
                </InputGroup>
              </div>
            </Col>
          </Row>
          <Row className='d-lg-none filter-mobile-syllabus-bar gx-0'>
            <Col xs={12}>
              <button
                type='button'
                className='filter-mobile-syllabus-toggle'
                onClick={() => setMobileSyllabusOpen((o) => !o)}
                aria-expanded={mobileSyllabusOpen}
                aria-controls='exam-paper-syllabus-panel'
                id='exam-paper-syllabus-toggle'
              >
                <span className='filter-mobile-syllabus-toggle-lead'>
                  <FaSlidersH className='filter-mobile-syllabus-toggle-icon' aria-hidden />
                  Syllabus filters
                </span>
                {mobileSyllabusOpen ? (
                  <FaChevronUp className='filter-mobile-syllabus-toggle-chevron' aria-hidden />
                ) : (
                  <FaChevronDown className='filter-mobile-syllabus-toggle-chevron' aria-hidden />
                )}
              </button>
            </Col>
          </Row>
          <Row className='filter-container'>
            <Col
              lg={4}
              xl={3}
              className={`${mobileSyllabusOpen ? 'd-block' : 'd-none'} d-lg-block`}
            >
              <div
                className='filter-sidebar'
                id='exam-paper-syllabus-panel'
                role='region'
                aria-label='Syllabus filters'
              >
                <FilterTopicTree
                  treeData={syllabusTree}
                  levelLabels={EXAM_SYLLABUS_LEVELS}
                  selectedChapters={selectedChapters}
                  setSelectedChapters={setSelectedChapters}
                />
                <div className='filter-btn-wrap'>
                  {/* <span role='button' className='text-btn' disabled={loading}>
                    Cancel
                  </span> */}
                  <Button
                    className='d-flex align-items-center gap-2 filter-apply-syllabus'
                    variant='primary'
                    onClick={() => handleApplyFilters()}
                  >
                    {loading && (
                      <span
                        className='spinner-border spinner-border-sm'
                        role='status'
                        aria-hidden='true'
                      ></span>
                    )}
                    Apply Filters
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={8} xl={9} className='position-relative'>
              {initialLoading || loading ? (
                (activeTab === 'all' || activeTab === 'bookmarked') &&
                resultsLayout === 'single' ? (
                  <ExamPaperTableSkeleton rows={6} />
                ) : (
                  <Row className='filter-content'>
                    {loaders.map((_, index) => (
                      <Col key={index} xl={12} xxl={6} className='mb-4'>
                        <BasicContentLoader />
                      </Col>
                    ))}
                  </Row>
                )
              ) : (
                <>
                  {(activeTab === 'all' || activeTab === 'bookmarked') && (
                    <div className='select-wrapper'>
                      {activeTab === 'all' && (
                        <>
                          <SelectedDropdown
                            id='mark-dropdown'
                            title='Mark:'
                            icon={checkTickIcon}
                            selectedOption={selectedMark}
                            options={marks}
                            onSelect={handleMarkSelect}
                            placeholder='All marks'
                          />
                          <SelectedDropdown
                            id='year-dropdown'
                            title='Select year:'
                            icon={checkTickIcon}
                            selectedOption={selectedYear}
                            options={years}
                            onSelect={handleYearSelect}
                            placeholder='Year to date'
                          />
                        </>
                      )}
                      <div className='layout-density-toggle'>
                        <span className='layout-density-label'>Layout:</span>
                        <ButtonGroup
                          size='sm'
                          aria-label='Question list column layout'
                        >
                          <Button
                            type='button'
                            variant={
                              resultsLayout === 'single'
                                ? 'primary'
                                : 'outline-secondary'
                            }
                            aria-pressed={resultsLayout === 'single'}
                            aria-label='Single column layout'
                            title='Single column'
                            onClick={() => setResultsLayout('single')}
                          >
                            <FaListUl
                              className='layout-density-icon'
                              aria-hidden
                            />
                          </Button>
                          <Button
                            type='button'
                            variant={
                              resultsLayout === 'two'
                                ? 'primary'
                                : 'outline-secondary'
                            }
                            aria-pressed={resultsLayout === 'two'}
                            aria-label='Two column layout'
                            title='Two columns'
                            onClick={() => setResultsLayout('two')}
                          >
                            <FaColumns
                              className='layout-density-icon'
                              aria-hidden
                            />
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  )}
                  {activeTab === 'all' &&
                    questions.length === 0 &&
                    !initialLoading &&
                    !loading && (
                      <h4 className='text-center mt-5'>No questions found.</h4>
                    )}
                  {activeTab === 'all' && questions.length > 0 && (
                    <AllComponent
                      selectedPapers={selectedPapers}
                      setSelectedPapers={setSelectedPapers}
                      setShowToastSnackbar={setShowToastSnackbar}
                      questions={questions}
                      allBookmarkedIds={allBookmarkedIds}
                      handleBookmarkClick={handleBookmarkClick}
                      handleQuestionClick={handleQuestionClick}
                      resultsLayout={resultsLayout}
                    />
                  )}
                  {activeTab === 'bookmarked' && (
                    <BookmarkedComponent
                      selectedPapers={selectedPapers}
                      setSelectedPapers={setSelectedPapers}
                      setShowToastSnackbar={setShowToastSnackbar}
                      bookmarkedQuestions={bookmarkedQuestions}
                      allBookmarkedIds={allBookmarkedIds}
                      setBookmarkedQuestions={setBookmarkedQuestions}
                      handleBookmarkClick={handleBookmarkClick}
                      loading={bookmarkLoading}
                      onQuestionCardClick={handleQuestionClick}
                      setShowModal={setShowModal}
                      setSelectedQuestion={setSelectedQuestion}
                      setQuestionDetailLoading={setQuestionDetailLoading}
                      resultsLayout={resultsLayout}
                    />
                  )}
                  {loadMoreLoading ? (
                    activeTab === 'all' &&
                    resultsLayout === 'single' ? (
                      <ExamPaperTableSkeleton rows={2} showHeader={false} />
                    ) : (
                      <Row className='filter-content'>
                        {loaders.map((_, index) => (
                          <Col key={index} xl={12} xxl={6} className='mb-4'>
                            <BasicContentLoader />
                          </Col>
                        ))}
                      </Row>
                    )
                  ) : null}

                  <div className='filter-footer'>
                    {activeTab === 'all' &&
                      pagination.currentPage < pagination.totalPages && (
                        <span
                          role='button'
                          className={`load-more-btn ${
                            loadMoreLoading ? 'rotate-icon' : ''
                          }`}
                          onClick={handleLoadMore}
                        >
                          {refreshIcon} Load more
                        </span>
                      )}
                    {activeTab === 'bookmarked' && (
                      <CustomPagination
                        currentPage={bookmarkPagination.currentPage}
                        totalPages={bookmarkPagination.totalPages}
                        onPageChange={(page) => {
                          fetchBookmarkedQuestions(page, searchText)
                        }}
                      />
                    )}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <QuestionDetailsModal
        show={showModal}
        handleClose={handleCloseModal}
        question={selectedQuestion}
        loading={questionDetailLoading}
      />
    </>
  )
}

export default FliterPappers
